import apiClient from '../utils/apiClient';
import { handleApiError } from '../utils/errorHandler';

export interface Category {
  _id: string;
  id?: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  image?: string;
  parent?: string | null;
  level: 1 | 2;
  order: number;
  isActive: boolean;
  isDeleted?: boolean;
  metaTitle?: string;
  metaDescription?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CategoryTreeNode {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  image?: string;
  order: number;
  children?: CategoryTreeNode[];
  accountTypes?: Array<{ id: string; name: string; image: string | null }>;
}

export interface CreateCategoryInput {
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  image?: string;
  parent?: string | null;
  order?: number;
  isActive?: boolean;
  metaTitle?: string;
  metaDescription?: string;
}

class CategoryService {
  list(opts: { activeOnly?: boolean; includeDeleted?: boolean } = {}): Promise<Category[]> {
    const q = new URLSearchParams();
    if (opts.activeOnly) q.append('activeOnly', 'true');
    if (opts.includeDeleted) q.append('includeDeleted', 'true');
    const qs = q.toString();
    return apiClient
      .get(`/admin/categories${qs ? `?${qs}` : ''}`)
      .then((r) => r.data?.data || r.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  tree(activeOnly = false): Promise<CategoryTreeNode[]> {
    return apiClient
      .get(`/admin/categories/tree?activeOnly=${activeOnly}`)
      .then((r) => r.data?.data || r.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  get(id: string): Promise<Category> {
    return apiClient
      .get(`/admin/categories/${id}`)
      .then((r) => r.data?.data || r.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  create(input: CreateCategoryInput): Promise<Category> {
    return apiClient
      .post('/admin/categories', input)
      .then((r) => r.data?.data || r.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  update(id: string, input: Partial<CreateCategoryInput>): Promise<Category> {
    return apiClient
      .put(`/admin/categories/${id}`, input)
      .then((r) => r.data?.data || r.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  remove(id: string): Promise<{ deleted: true; cascadedChildren: number; detachedAccountTypes: number }> {
    return apiClient
      .delete(`/admin/categories/${id}`)
      .then((r) => r.data?.data || r.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  restore(id: string): Promise<Category> {
    return apiClient
      .post(`/admin/categories/${id}/restore`)
      .then((r) => r.data?.data || r.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  reorder(updates: Array<{ id: string; order: number }>): Promise<void> {
    return apiClient
      .post('/admin/categories/reorder', { updates })
      .then(() => undefined)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  setAccountTypes(categoryId: string, accountTypeIds: string[]): Promise<{ added: number; removed: number }> {
    return apiClient
      .put(`/admin/categories/${categoryId}/account-types`, { accountTypeIds })
      .then((r) => r.data?.data || r.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }
}

export default new CategoryService();
